import { Worker } from "node:worker_threads";
import type { WorkerModuleReference } from "../types/job.js";
import { serializeError } from "../utils/errors.js";
import { normalizeWorkerReference } from "./workerBridge.js";

type PendingWorker = {
  run: () => void;
};

export class WorkerPool {
  private busy = 0;
  private readonly pending: PendingWorker[] = [];
  private readonly liveWorkers = new Set<Worker>();

  constructor(private readonly maxWorkers: number) {}

  getStats(): { max: number; busy: number; idle: number } {
    return {
      max: this.maxWorkers,
      busy: this.busy,
      idle: Math.max(0, this.maxWorkers - this.busy),
    };
  }

  run<TPayload, TResult>(options: {
    worker: WorkerModuleReference;
    payload: TPayload;
    jobId: string;
    name: string;
    attempt: number;
    timeoutMs: number;
    createdAt: string;
    startedAt: string;
  }): Promise<TResult> {
    return new Promise((resolve, reject) => {
      const execute = () => {
        this.busy += 1;
        const normalized = normalizeWorkerReference(options.worker);
        const worker = new Worker(getWorkerBridgeUrl(), {
          workerData: {
            modulePath: normalized.modulePath,
            exportName: normalized.exportName,
            payload: options.payload,
            jobId: options.jobId,
            name: options.name,
            attempt: options.attempt,
            createdAt: options.createdAt,
            startedAt: options.startedAt,
          },
        });
        this.liveWorkers.add(worker);

        let settled = false;
        const timer = setTimeout(() => {
          if (settled) return;
          settled = true;
          void worker.terminate();
          reject(new Error(`Worker timed out after ${options.timeoutMs}ms`));
          this.finish(worker);
        }, options.timeoutMs);
        timer.unref?.();

        worker.once("message", (message: { ok: boolean; result?: TResult; error?: unknown }) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          if (message.ok) resolve(message.result as TResult);
          else reject(serializeError(message.error));
          void worker.terminate();
          this.finish(worker);
        });

        worker.once("error", (error) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(error);
          this.finish(worker);
        });

        worker.once("exit", (code) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          if (code === 0) resolve(undefined as TResult);
          else reject(new Error(`Worker exited with code ${code}`));
          this.finish(worker);
        });
      };

      if (this.busy < this.maxWorkers) execute();
      else this.pending.push({ run: execute });
    });
  }

  async shutdown(): Promise<void> {
    this.pending.length = 0;
    await Promise.all([...this.liveWorkers].map((worker) => worker.terminate()));
    this.liveWorkers.clear();
    this.busy = 0;
  }

  private finish(worker: Worker): void {
    this.liveWorkers.delete(worker);
    this.busy = Math.max(0, this.busy - 1);
    const next = this.pending.shift();
    if (next) queueMicrotask(next.run);
  }
}

let workerBridgeUrl: URL | undefined;

function getWorkerBridgeUrl(): URL {
  workerBridgeUrl ??= new URL(
    `data:text/javascript;charset=utf-8,${encodeURIComponent(`
      import { parentPort, workerData } from "node:worker_threads";

      async function run() {
        const mod = await import(workerData.modulePath);
        const worker = mod[workerData.exportName];
        if (typeof worker !== "function") {
          throw new Error(\`Worker export "\${workerData.exportName}" was not found or is not a function\`);
        }
        const result = await worker({
          jobId: workerData.jobId,
          name: workerData.name,
          payload: workerData.payload,
          attempt: workerData.attempt,
          signal: new AbortController().signal,
          createdAt: new Date(workerData.createdAt),
          startedAt: new Date(workerData.startedAt)
        });
        parentPort?.postMessage({ ok: true, result });
      }

      run().catch((error) => {
        parentPort?.postMessage({
          ok: false,
          error: error instanceof Error
            ? { name: error.name, message: error.message, stack: error.stack }
            : error
        });
      });
    `)}`
  );
  return workerBridgeUrl;
}
